import { Api } from "./Api.js";
import { LiveStore } from "./LiveStore.js";
import type { AppContext, Page } from "./Page.js";
import { h } from "./dom.js";
import { AlertCenter } from "./components/AlertCenter.js";
import { AlertsPage } from "./pages/AlertsPage.js";
import { CraftsPage } from "./pages/CraftsPage.js";
import { FlowsPage } from "./pages/FlowsPage.js";
import { HomePage } from "./pages/HomePage.js";
import { LogPage } from "./pages/LogPage.js";
import { NetworkPage } from "./pages/NetworkPage.js";
import { StoragePage } from "./pages/StoragePage.js";
import { PatternsPage } from "./pages/PatternsPage.js";
import { PowerPage } from "./pages/PowerPage.js";
import { RequestPage } from "./pages/RequestPage.js";
import { SettingsPage } from "./pages/SettingsPage.js";
import { StockPage } from "./pages/StockPage.js";
import { butlerVersionStatus } from "../shared/butlerVersion.js";

type PageClass = new (ctx: AppContext) => Page;

const ROUTES: Array<{ path: string; label: string; page: PageClass }> = [
  { path: "home", label: "Home", page: HomePage },
  { path: "stock", label: "Stock", page: StockPage },
  { path: "flows", label: "Flows", page: FlowsPage },
  { path: "crafts", label: "Crafts", page: CraftsPage },
  { path: "request", label: "Request", page: RequestPage },
  { path: "patterns", label: "Patterns", page: PatternsPage },
  { path: "power", label: "Power", page: PowerPage },
  { path: "log", label: "Log", page: LogPage },
  { path: "alerts", label: "Alerts", page: AlertsPage },
  { path: "network", label: "Network", page: NetworkPage },
  { path: "storage", label: "Storage", page: StoragePage },
  { path: "settings", label: "Settings", page: SettingsPage },
];

/** The dashboard shell: login, navigation, hash routing and toasts. */
class Dashboard implements AppContext {
  readonly api = new Api();
  readonly live = new LiveStore(() => this.api.snapshot());
  private readonly root = document.getElementById("app")!;
  private readonly main = h("main");
  private readonly nav = h("nav");
  private readonly linkBadge = h("span", { class: "link-badge" });
  private readonly toasts = h("div", { class: "toasts", role: "status" });
  private current: Page | null = null;
  private alerts: AlertCenter | null = null;

  async start(): Promise<void> {
    document.body.appendChild(this.toasts);
    this.api.onUnauthorized = () => this.showLogin();
    const { authenticated } = await this.api.session();
    if (authenticated) this.showApp();
    else this.showLogin();
  }

  toast(message: string, kind: "info" | "error" = "info"): void {
    const el = h("div", { class: `toast ${kind}` }, message);
    this.toasts.appendChild(el);
    setTimeout(() => el.remove(), kind === "error" ? 7000 : 3500);
  }

  navigate(route: string): void {
    location.hash = `#/${route}`;
  }

  private showLogin(): void {
    this.live.disconnect();
    this.current?.unmount();
    this.current = null;
    const password = h("input", { type: "password", autocomplete: "current-password", required: true, placeholder: "Password" });
    const error = h("p", { class: "error" });
    const form = h(
      "form",
      {
        class: "login card",
        onsubmit: async (e: Event) => {
          e.preventDefault();
          try {
            await this.api.login(password.value);
            this.showApp();
          } catch (err) {
            error.textContent = (err as Error).message;
            password.select();
          }
        },
      },
      h("h1", null, "OC Butler"),
      password,
      h("button", { class: "primary", type: "submit" }, "Log in"),
      error,
    );
    this.root.replaceChildren(form);
    password.focus();
  }

  private showApp(): void {
    for (const r of ROUTES) this.nav.appendChild(h("a", { href: `#/${r.path}`, "data-route": r.path }, r.label));
    this.alerts = new AlertCenter(this);
    this.root.replaceChildren(h("header", { class: "top" }, h("strong", { class: "brand" }, "OC Butler"), this.nav, this.alerts.bell, this.linkBadge), this.main, this.alerts.stack);
    this.live.on("link", () => this.renderLink());
    this.live.on("socket", () => this.renderLink());
    this.live.connect();
    window.addEventListener("hashchange", () => this.route());
    this.renderLink();
    this.route();
  }

  private renderLink(): void {
    const link = this.live.link;
    if (!link) {
      this.linkBadge.className = "link-badge stale";
      this.linkBadge.textContent = "Connecting…";
      return;
    }
    const version = butlerVersionStatus(link.version);
    const outdated = link.connected && version.state === "outdated";
    this.linkBadge.className = `link-badge ${link.connected ? "on" : "off"}${outdated ? " outdated" : ""}`;
    this.linkBadge.textContent = link.connected
      ? `Game: ${link.butler} v${link.version ?? "?"}${outdated ? ` (${version.behind} behind)` : ""}`
      : "Game offline";
    this.linkBadge.title =
      (outdated ? `Butler outdated: latest is v${version.latest}. Details on the Network page.\n` : "") +
      (this.live.socketOpen ? "Live updates on" : "Live updates unavailable, refreshing every 5 s");
  }

  private route(): void {
    // Groups now live on the Stock page; old links still land there.
    const path = location.hash.replace(/^#\/?/, "").replace(/^groups$/, "stock") || "home";
    // Missing inputs became a tab of Patterns; old links still land there.
    if (path === "missing") {
      history.replaceState(null, "", "#/patterns/missing");
      return this.route();
    }
    // "patterns/missing": the page reads the rest of the path itself.
    const route = ROUTES.find((r) => r.path === path.split("/")[0]) ?? ROUTES[0];
    for (const a of this.nav.querySelectorAll("a")) a.classList.toggle("active", a.dataset.route === route.path);
    this.current?.unmount();
    const page = new route.page(this);
    this.current = page;
    this.main.replaceChildren(page.el);
    document.title = `${page.title} · OC Butler`;
    this.alerts?.render();
    void page.mount();
  }
}

void new Dashboard().start();
